// Phase 2: レッスン配置（LessonPlacement）エディタ
// 旧 LessonEditPage の ScheduleTable と同じ CSS クラス構造に揃えた版。
// Phase 1.2 で講師・時間がマスター属性に移動したため、配置カードは
// 「マスター選択 + メモ + 予約停止」のみ。時間・講師はマスターから自動表示（read-only）。
//
// 利用クラス: editarea.schedule > form > schedule-table > schedule-grid > day-column ...
//   定義: styles/scss/common.scss (291行〜)
//
// upsert 仕様: lesson_model_redesign_memo.md 8.4 / 9.7

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { HexColorPicker } from "react-colorful";
import DragScrollContainer from "../../../../components/common/DragScrollContainer";
import {
  fetchLessonPlacements,
  upsertLessonPlacements,
  updateLessonContent,
} from "../../../../utils/lessonV2Api";

const DAYS = [
  { key: "monday", label: "月" },
  { key: "tuesday", label: "火" },
  { key: "wednesday", label: "水" },
  { key: "thursday", label: "木" },
  { key: "friday", label: "金" },
  { key: "saturday", label: "土" },
  // 旧 LessonEditPage に合わせて日曜は省略。必要なら追加可
];

// Phase 1.2: instructor_id / start_time / end_time は配置側に持たない（マスターから取得）
// Phase 1.3: space_flg を追加（旧 UI の Space ボタン相当）
const emptyRowTemplate = {
  id: null,
  content_id: "",
  day_of_week: "monday",
  memo: "",
  close_flg: false,
  space_flg: false,
  display_order: 0,
};

let _keySeq = 1;
const makeKey = () => {
  _keySeq += 1;
  return _keySeq;
};

const LessonPlacementEditor = ({
  studioId,
  contents,
  instructors,
  busy,
  setBusy,
  onMessage,
  onContentsChanged,
}) => {
  const [rows, setRows] = useState([]);
  const [deletedIds, setDeletedIds] = useState([]);
  // 配置カードから編集したマスター色のローカル上書き（保存時に内容マスターへ反映）
  // { [content_id]: color }
  const [colorEdits, setColorEdits] = useState({});
  // ロード時に自スタジオで使われていた content_id（ローカル編集で変えても固定）
  // ※「他スタジオで使われている」content_id を判定する基準として使う
  const [initialOwnContentIds, setInitialOwnContentIds] = useState(
    () => new Set(),
  );

  const load = useCallback(async () => {
    if (!studioId) return;
    setBusy(true);
    try {
      const data = await fetchLessonPlacements(studioId);
      // Phase 1.2: instructor/time はマスター由来（content 経由でレスポンスに含まれる）。
      // ローカル state は配置自体の属性だけ保持。
      const loadedRows = (data.placements || []).map((p) => ({
        id: p.id,
        content_id: p.content_id || "",
        day_of_week: p.day_of_week,
        memo: p.memo || "",
        close_flg: !!p.close_flg,
        space_flg: !!p.space_flg, // Phase 1.3
        display_order: p.display_order || 0,
        _client_key: makeKey(),
      }));
      setRows(loadedRows);
      setDeletedIds([]);
      // 自スタジオで「ロード時に」使われていた content_id（変更しない固定スナップショット）
      setInitialOwnContentIds(
        new Set(
          (data.placements || [])
            .filter((p) => !p.space_flg && p.content_id)
            .map((p) => String(p.content_id)),
        ),
      );
    } catch (err) {
      onMessage &&
        onMessage(err.message || "配置の取得に失敗しました", "error");
    } finally {
      setBusy(false);
    }
  }, [studioId, setBusy, onMessage]);

  useEffect(() => {
    load();
  }, [load]);

  // 曜日ごとに行をグルーピング（display_order でソート）
  // さらに、全曜日の最大行数に合わせて、足りない曜日の末尾に仮想 Space を埋める。
  // 仮想 Space は描画用のみ（_virtual=true）、DB 保存対象外。
  const rowsByDay = useMemo(() => {
    const map = {};
    DAYS.forEach((d) => {
      map[d.key] = [];
    });
    for (const r of rows) {
      if (map[r.day_of_week]) {
        map[r.day_of_week].push(r);
      }
    }
    Object.keys(map).forEach((day) => {
      map[day].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    });
    // 行揃え: 全曜日の最大行数に合わせて、不足分を仮想 Space で埋める
    const maxRows = DAYS.reduce((m, d) => Math.max(m, map[d.key].length), 0);
    DAYS.forEach((d) => {
      while (map[d.key].length < maxRows) {
        const idx = map[d.key].length;
        map[d.key].push({
          _client_key: `virtual-${d.key}-${idx}`,
          _virtual: true,
          space_flg: true,
          day_of_week: d.key,
          content_id: "",
          memo: "",
          close_flg: false,
          display_order: idx,
        });
      }
    });
    return map;
  }, [rows]);

  // 「他スタジオで使われている」content_id Set
  // server の c.in_use=true から、ロード時に自スタジオで使っていたものを除いたもの
  // → ローカルでドロップダウンを別の値に変えても元の content が選択肢から復活する
  const otherStudioUsedContentIds = useMemo(() => {
    return new Set(
      contents
        .filter((c) => c.in_use && !initialOwnContentIds.has(String(c.id)))
        .map((c) => String(c.id)),
    );
  }, [contents, initialOwnContentIds]);

  // 現在の rows で使われている content_id Set（self 含む、PlacementItem 内で isSelf チェック）
  const localUsedContentIds = useMemo(() => {
    return new Set(
      rows
        .filter((r) => !r.space_flg && r.content_id)
        .map((r) => String(r.content_id)),
    );
  }, [rows]);

  const handleFieldChange = (clientKey, key, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r._client_key === clientKey ? { ...r, [key]: value } : r,
      ),
    );
  };

  // 配置カードでの色編集（内容マスターの色を上書き予約。保存時に反映）
  const handleColorChange = (contentId, value) => {
    if (!contentId) return;
    setColorEdits((prev) => ({ ...prev, [String(contentId)]: value }));
  };

  // Phase 1.3: isSpace=true で Space 行（content なし、空セル）を追加
  // afterIndex は rowsByDay 上のインデックス（仮想 Space 含む）。仮想位置（実 row より大きい）の
  // 場合は末尾追加と同じ扱いに調整。
  const addRow = (dayKey, afterIndex = -1, isSpace = false) => {
    const sameDay = rows
      .filter((r) => r.day_of_week === dayKey)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const newRow = {
      ...emptyRowTemplate,
      day_of_week: dayKey,
      space_flg: !!isSpace,
      _client_key: makeKey(),
    };

    let nextRows;
    if (afterIndex === -2) {
      nextRows = [newRow, ...rows];
    } else if (afterIndex === -1 || afterIndex >= sameDay.length) {
      // 末尾追加 or 仮想行位置からの追加 → どちらも曜日末尾に
      nextRows = [...rows, newRow];
    } else {
      const target = sameDay[afterIndex];
      const insertAt = target ? rows.indexOf(target) + 1 : rows.length;
      nextRows = [...rows.slice(0, insertAt), newRow, ...rows.slice(insertAt)];
    }
    const reordered = [];
    const orderCounters = {};
    for (const r of nextRows) {
      const day = r.day_of_week;
      orderCounters[day] = orderCounters[day] || 0;
      reordered.push({ ...r, display_order: orderCounters[day] });
      orderCounters[day] += 1;
    }
    setRows(reordered);
  };

  // Phase 1.3: Space 追加のショートカット
  const addSpace = (dayKey, afterIndex = -1) =>
    addRow(dayKey, afterIndex, true);

  const removeRow = (row) => {
    if (row.id) {
      setDeletedIds((prev) => [...prev, row.id]);
    }
    setRows((prev) => prev.filter((r) => r._client_key !== row._client_key));
  };

  // 親 (LessonManagementPage) から呼ばれる保存処理
  const handleSave = useCallback(async () => {
    // Phase 1.3: Space (space_flg=true) は content_id 不要
    // Phase 1.4: 同じ content_id を別配置に重複指定しない（9.8）
    const seenContentIds = new Set();
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      if (!r.space_flg && !r.content_id) {
        onMessage && onMessage("レッスン内容が未選択の配置があります", "error");
        return;
      }
      if (!r.space_flg && r.content_id) {
        const cid = String(r.content_id);
        if (seenContentIds.has(cid)) {
          onMessage &&
            onMessage(
              "同じレッスン内容が複数の配置で指定されています。同じ内容を別の配置に入れたい場合は、別マスターとして作成してください。",
              "error",
            );
          return;
        }
        seenContentIds.add(cid);
      }
    }
    setBusy(true);
    try {
      // Phase 1.2: payload から instructor_id / start_time / end_time を除外
      // Phase 1.3: space_flg を含める。Space 行は content_id null
      const payloadPlacements = rows.map((r, idx) => ({
        id: r.id,
        content_id: r.space_flg ? null : Number(r.content_id),
        day_of_week: r.day_of_week,
        memo: r.space_flg ? "" : r.memo || "",
        close_flg: r.space_flg ? false : !!r.close_flg,
        space_flg: !!r.space_flg,
        display_order: r.display_order != null ? r.display_order : idx,
      }));
      await upsertLessonPlacements({
        studioId: Number(studioId),
        placements: payloadPlacements,
        deletedPlacementIds: deletedIds,
      });
      // 配置カードで編集された色を内容マスター（LessonContent）へ反映。
      // 元の色と異なるものだけ color のみの部分更新で送る。
      const changedColors = Object.entries(colorEdits).filter(([cid, col]) => {
        const orig = contents.find((c) => String(c.id) === String(cid));
        return orig && (orig.color || "") !== (col || "");
      });
      for (const [cid, col] of changedColors) {
        await updateLessonContent(Number(cid), { color: col });
      }
      onMessage && onMessage("保存しました", "success");
      // 色を変更した場合は内容マスターを再取得して全配置の表示へ反映
      if (changedColors.length && typeof onContentsChanged === "function") {
        await onContentsChanged();
      }
      setColorEdits({});
      await load();
    } catch (err) {
      onMessage && onMessage(err.message || "保存に失敗しました", "error");
    } finally {
      setBusy(false);
    }
  }, [
    rows,
    deletedIds,
    studioId,
    setBusy,
    onMessage,
    load,
    colorEdits,
    contents,
    onContentsChanged,
  ]);

  // 保存ハンドラを ref で公開しないため、親に上げる
  useEffect(() => {
    if (typeof onMessage === "function") {
      // no-op
    }
  }, [onMessage]);

  // 親から呼べるよう window へ一時公開する代わりに、props.saveHandlerRef を使う
  // → シンプルさのため、ここでは「親が onSave 関数を内側から取れる」ように
  //   handleSave を effect で親 setter に渡す（後段 LessonManagementPage 参照）
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__lessonPlacementSave = handleSave;
    }
    return () => {
      if (
        typeof window !== "undefined" &&
        window.__lessonPlacementSave === handleSave
      ) {
        window.__lessonPlacementSave = null;
      }
    };
  }, [handleSave]);

  if (!studioId) {
    return (
      <p style={{ color: "rgba(255,255,255,0.5)" }}>
        スタジオを選択してください
      </p>
    );
  }

  return (
    <DragScrollContainer className="schedule-table">
      <div className="schedule-grid">
        {DAYS.map((day) => (
          <DayColumn
            key={day.key}
            day={day}
            rows={rowsByDay[day.key] || []}
            contents={contents}
            otherStudioUsedContentIds={otherStudioUsedContentIds}
            localUsedContentIds={localUsedContentIds}
            busy={busy}
            colorEdits={colorEdits}
            onFieldChange={handleFieldChange}
            onColorChange={handleColorChange}
            onAddRow={addRow}
            onAddSpace={addSpace}
            onRemoveRow={removeRow}
          />
        ))}
      </div>
    </DragScrollContainer>
  );
};

// クリック時のドラッグスクロール抑止（旧 LessonEditPage と同じパターン）
const wrapButtonClick = (callback) => (e) => {
  const container = e.target.closest(".drag-scroll-container");
  if (container && container.dataset.clickDisabled === "true") {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  callback();
};

const DayColumn = ({
  day,
  rows,
  contents,
  otherStudioUsedContentIds,
  localUsedContentIds,
  busy,
  colorEdits,
  onFieldChange,
  onColorChange,
  onAddRow,
  onAddSpace,
  onRemoveRow,
}) => {
  return (
    <div className="day-column">
      <div className="day-header">
        <h3>{day.label}</h3>
      </div>
      <div className="lessons-list">
        <div className="day-actions">
          <button
            type="button"
            className="add-btn"
            disabled={busy}
            onClick={wrapButtonClick(() => onAddRow(day.key, -2))}
          >
            + Add
          </button>
          {/* Phase 1.3: Space ボタン（旧 UI と同じ位置） */}
          <button
            type="button"
            className="space-btn"
            disabled={busy}
            onClick={wrapButtonClick(() => onAddSpace(day.key, -2))}
          >
            Space
          </button>
        </div>
        {rows.map((row, index) => (
          <PlacementItem
            key={row._client_key}
            row={row}
            index={index}
            dayKey={day.key}
            contents={contents}
            otherStudioUsedContentIds={otherStudioUsedContentIds}
            localUsedContentIds={localUsedContentIds}
            busy={busy}
            colorEdits={colorEdits}
            onFieldChange={onFieldChange}
            onColorChange={onColorChange}
            onAddRow={onAddRow}
            onAddSpace={onAddSpace}
            onRemoveRow={onRemoveRow}
          />
        ))}
      </div>
    </div>
  );
};

const PlacementItem = ({
  row,
  index,
  dayKey,
  contents,
  otherStudioUsedContentIds,
  localUsedContentIds,
  busy,
  colorEdits,
  onFieldChange,
  onColorChange,
  onAddRow,
  onAddSpace,
  onRemoveRow,
}) => {
  // カラーピッカーの開閉（hooks は早期 return より前に宣言する必要がある）
  const [pickerOpen, setPickerOpen] = useState(false);

  // Phase 1.3: Space 行は空セル表示（旧 UI の lesson-item.space と同じ class）
  // 仮想 Space (_virtual=true) は他曜日の行数に合わせた自動補完。操作不可・保存対象外。
  if (row.space_flg) {
    const isVirtual = !!row._virtual;
    return (
      <div className="lesson-item space">
        <div className="lesson-content space">
          <div className="space-item">
            <span style={isVirtual ? { opacity: 0.5 } : null}>Space</span>
          </div>
        </div>
        {!isVirtual && (
          <div className="space-actions">
            <button
              type="button"
              className="add-btn"
              disabled={busy}
              onClick={wrapButtonClick(() => onAddRow(dayKey, index))}
            >
              + Add
            </button>
            <button
              type="button"
              className="space-btn"
              disabled={busy}
              onClick={wrapButtonClick(() => onAddSpace(dayKey, index))}
            >
              Space
            </button>
            <button
              type="button"
              className="delete-btn"
              disabled={busy}
              onClick={wrapButtonClick(() => onRemoveRow(row))}
            >
              × Delete
            </button>
          </div>
        )}
      </div>
    );
  }

  const selectedContent = contents.find(
    (c) => String(c.id) === String(row.content_id),
  );
  // 表示する色：ローカル編集（未保存）があればそれを優先、無ければマスターの色
  const editedColor =
    colorEdits && colorEdits[String(row.content_id)] != null
      ? colorEdits[String(row.content_id)]
      : null;
  const color = editedColor != null ? editedColor : selectedContent?.color || "";
  // Phase 1.2: 時間・講師はマスター属性。配置カードは表示のみ
  // 未選択時は空白だが、縦幅維持のため後段で NBSP( ) を入れる
  const contentTimeRange =
    selectedContent && selectedContent.start_time && selectedContent.end_time
      ? `${selectedContent.start_time} - ${selectedContent.end_time}`
      : "";
  const contentInstructor = selectedContent?.instructor_name || "";
  const contentName = selectedContent?.name || "";
  const contentTarget = selectedContent?.target || "";
  const isYoung = !!selectedContent?.young_flg;

  return (
    <div className={`lesson-item ${row.close_flg ? "closed" : ""}`}>
      <div className="lesson-content">
        <div className="lesson-form">
          {/* レッスン内容（旧UIの「ジャンル/対象」を LessonContent 選択に置換）
              Phase 1.4: 既に他配置で使われている内容は表示しない（9.8）
              判定ロジック:
                - 自分が選んでいる → 常に表示
                - 他スタジオで使われている（server in_use && ロード時に自スタジオ未使用） → 非表示
                - 現在の rows で他の placement が使っている → 非表示
                - それ以外 → 表示
              これにより、ローカルで選択を外した content も即座に再選択可能になる。 */}
          {(() => {
            const selfCid = String(row.content_id);
            const availableContents = contents.filter((c) => {
              const cid = String(c.id);
              if (cid === selfCid) return true;
              if (
                otherStudioUsedContentIds &&
                otherStudioUsedContentIds.has(cid)
              )
                return false;
              if (localUsedContentIds && localUsedContentIds.has(cid))
                return false;
              return true;
            });
            const placeholder =
              availableContents.length === 0
                ? "未選択のレッスンがありません"
                : "レッスン内容を選択";
            return (
              <select
                value={row.content_id || ""}
                onChange={(e) =>
                  onFieldChange(row._client_key, "content_id", e.target.value)
                }
                disabled={busy}
              >
                <option value="">{placeholder}</option>
                {availableContents.map((c) => {
                  // ラベル形式: 「HIPHOP（入門🔰）19:15-20:15 HINA」
                  const parts = [c.name];
                  if (c.target) parts.push(`（${c.target}）`);
                  if (c.start_time && c.end_time) {
                    parts.push(` ${c.start_time}-${c.end_time}`);
                  }
                  if (c.instructor_name) {
                    parts.push(` ${c.instructor_name}`);
                  }
                  return (
                    <option key={c.id} value={c.id}>
                      {parts.join("")}
                    </option>
                  );
                })}
              </select>
            );
          })()}

          {/* 内容の概要（read-only）: 1行目 名前（対象） / 2行目 時間 講師 / 幼少は右上バッジ。
              名前・対象・時間・講師の変更は内容マスターで行う。 */}
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: "4px 6px",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 2,
              fontSize: 11,
            }}
            title="レッスン名・対象・時間・講師はマスターで設定（変更したい場合は内容マスターを編集）"
          >
            {isYoung && (
              <span
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  fontSize: 10,
                  lineHeight: 1.4,
                  padding: "0 6px",
                  borderRadius: 999,
                  color: "#ecff82",
                  background: "rgba(236,255,130,0.14)",
                  border: "1px solid rgba(236,255,130,0.4)",
                  whiteSpace: "nowrap",
                }}
              >
                幼少
              </span>
            )}
            {/* 1行目: 名前（対象）。右上バッジと重ならないよう余白確保 */}
            <span style={{ paddingRight: 34 }}>
              <span style={{ color: "#fff", fontWeight: 600 }}>
                {contentName || " "}
              </span>
              {contentTarget && (
                <span style={{ color: "rgba(255,255,255,0.7)" }}>
                  （{contentTarget}）
                </span>
              )}
            </span>
            {/* 2行目: 時間 講師（縦幅維持のため NBSP フォールバック） */}
            <span style={{ color: "rgba(255,255,255,0.85)" }}>
              {contentTimeRange || " "}
              {contentInstructor && (
                <span style={{ color: "rgba(255,255,255,0.7)", marginLeft: 6 }}>
                  {contentInstructor}
                </span>
              )}
            </span>
          </div>

          {/* 色（編集可。保存時に内容マスター LessonContent の色へ反映される） */}
          <div className="color-inputs">
            <div
              className="color-preview"
              style={{
                backgroundColor: color || "#ffffff",
                width: "30px",
                height: "20px",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "2px",
                cursor: selectedContent && !busy ? "pointer" : "default",
              }}
              title={
                selectedContent
                  ? "クリックで色を選択（保存で内容マスターに反映）"
                  : "内容未選択"
              }
              onClick={() =>
                selectedContent && !busy && setPickerOpen((v) => !v)
              }
            />
            <input
              type="text"
              value={color}
              onChange={(e) => onColorChange(row.content_id, e.target.value)}
              placeholder={selectedContent ? "例：#60dfff" : "内容未選択"}
              style={{ flex: 1 }}
              disabled={busy || !selectedContent}
              data-verify-color={color}
              data-verify-color-dirty={editedColor != null ? "true" : "false"}
            />
          </div>

          {pickerOpen && selectedContent && (
            <div className="color-picker-container">
              <div
                className="color-picker-overlay"
                onClick={() => setPickerOpen(false)}
              />
              <div className="color-picker-popup">
                <HexColorPicker
                  color={color || "#ffffff"}
                  onChange={(c) => onColorChange(row.content_id, c)}
                />
                <div className="color-picker-hex">HEX: {color || "#ffffff"}</div>
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  className="color-picker-close"
                >
                  完了
                </button>
              </div>
            </div>
          )}

          {/* メモ */}
          <input
            type="text"
            value={row.memo || ""}
            onChange={(e) =>
              onFieldChange(row._client_key, "memo", e.target.value)
            }
            placeholder="メモ"
            className="memo-input"
            disabled={busy}
          />

          {/* 予約停止（幼少クラスは Phase 6 で対応のため非表示） */}
          <div className="close-flag-input">
            <label>
              <input
                type="checkbox"
                checked={row.close_flg || false}
                onChange={(e) =>
                  onFieldChange(row._client_key, "close_flg", e.target.checked)
                }
                disabled={busy}
              />
              予約停止
            </label>
          </div>
        </div>
      </div>
      <div className="lesson-actions">
        <button
          type="button"
          className="add-btn"
          disabled={busy}
          onClick={wrapButtonClick(() => onAddRow(dayKey, index))}
        >
          + Add
        </button>
        <button
          type="button"
          className="space-btn"
          disabled={busy}
          onClick={wrapButtonClick(() => onAddSpace(dayKey, index))}
        >
          Space
        </button>
        <button
          type="button"
          className="delete-btn"
          disabled={busy}
          onClick={wrapButtonClick(() => onRemoveRow(row))}
        >
          × Delete
        </button>
      </div>
    </div>
  );
};

export default LessonPlacementEditor;
